import {
  type UniformShadeCharacteristics,
  type ShadeCharacteristics,
  isPartialMultiShadeCharacteristics,
  type DeepPartial,
} from "@jasper/client-models";
import type { ValidationErrors } from "../../types";
import { Select, Stack, Text } from "@mantine/core";
import { ButtonSelect, ButtonToggle } from "../../components";
import { MultiShadeFormV1 } from "./MultiShadeFormV1";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { normalizeShadeCharacteristics } from "./utils";
import type { ShadeCharacteristicsFormProps } from "./ShadeCharacteristicsForm";

export const ShadeCharacteristicsFormV1 = ({
  value,
  errors,
  onChange,
  itemShadeTypes,
  itemShades,
}: ShadeCharacteristicsFormProps) => {
  const { t } = useTranslation("forms");

  const selectableShade = useMemo(
    () => itemShades.filter(({ shadeType }) => shadeType === value?.shadeType),
    [itemShades, value?.shadeType]
  );

  const handleSubmit = (next: DeepPartial<ShadeCharacteristics>) =>
    onChange(normalizeShadeCharacteristics({ value: next, itemShades }));

  return (
    <Stack w="100%" gap="md">
      <Text size="sm" style={{ fontWeight: "bold" }}>
        {t("forms.shadeCharacteristics.labels.shadeType")}
      </Text>
      <ButtonSelect
        value={value?.shadeType}
        onChange={shadeType =>
          // Shades are filtered by shade type, so reset the selected shade(s)
          // whenever the shade type changes.
          handleSubmit({
            ...value,
            shadeType,
            shade: undefined,
            baseShade: undefined,
            incisalShade: undefined,
            gingivalShade: undefined,
          })
        }
        data={itemShadeTypes}
        error={
          errors?.shadeType
            ? t("forms.shadeCharacteristics.errors.shadeType")
            : undefined
        }
      />

      <Text
        size="sm"
        style={{
          fontWeight: "bold",
        }}
      >
        {t("forms.shadeCharacteristics.labels.isMultiShade")}
      </Text>
      <ButtonToggle
        value={value?.isMultiShade}
        onChange={isMultiShade => handleSubmit({ ...value, isMultiShade })}
        trueLabel={t("common.yes")}
        falseLabel={t("common.no")}
      />

      {value && isPartialMultiShadeCharacteristics(value) ? (
        <MultiShadeFormV1
          disabled={!value || !value.shadeType}
          value={value}
          errors={errors}
          onChange={multiShade => handleSubmit({ ...value, ...multiShade })}
          itemShades={selectableShade}
        />
      ) : (
        <Stack>
          <Select
            label={
              <Text size="sm" style={{ fontWeight: "bold" }}>
                {t("forms.shadeCharacteristics.labels.shade")}
              </Text>
            }
            // `null` (not `undefined`) keeps the Select controlled so it
            // clears when the shade is reset (e.g. on shade type change).
            value={value?.shade ?? null}
            data={selectableShade}
            name="Shade"
            placeholder={t("forms.shadeCharacteristics.placeholders.shade")}
            disabled={!value || !value.shadeType}
            onChange={shade =>
              handleSubmit({ ...value, shade: shade ?? undefined })
            }
            error={
              (errors as ValidationErrors<UniformShadeCharacteristics>)?.shade
                ? t("forms.shadeCharacteristics.errors.shade")
                : undefined
            }
          />
        </Stack>
      )}
    </Stack>
  );
};
